/**
 * Mock Telegram WebApp для локальной разработки в обычном браузере.
 *
 * Активируется когда VITE_MOCK_TG === '1' и нет нативного window.Telegram.WebApp.
 * Инжектит фейковый WebApp с заглушками BackButton / MainButton / HapticFeedback
 * и формирует initData в формате application/x-www-form-urlencoded,
 * который примет бэк с IA_DEV_TG_BYPASS=true (TelegramAuthService::validate()).
 */

interface MockUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code: string;
}

function defaultMockUser(): MockUser {
  return {
    id: Number(import.meta.env.VITE_MOCK_TG_USER_ID ?? 424242),
    first_name: import.meta.env.VITE_MOCK_TG_FIRST_NAME ?? 'Dev',
    last_name: import.meta.env.VITE_MOCK_TG_LAST_NAME ?? 'User',
    username: import.meta.env.VITE_MOCK_TG_USERNAME ?? 'dev_user',
    language_code: import.meta.env.VITE_MOCK_TG_LANG ?? 'en',
  };
}

function buildInitData(user: MockUser): string {
  const authDate = Math.floor(Date.now() / 1000);
  const params = new URLSearchParams();
  params.set('user', JSON.stringify(user));
  params.set('auth_date', String(authDate));
  params.set('query_id', 'mock_' + Math.random().toString(36).slice(2, 10));
  params.set('hash', '0'.repeat(64));
  return params.toString();
}

export function installMockTelegram(): void {
  if (typeof window === 'undefined') return;
  if (window.Telegram?.WebApp?.initData) return;
  if (import.meta.env.VITE_MOCK_TG !== '1') return;

  const user = defaultMockUser();
  const initData = buildInitData(user);
  const noop = () => { /* mock */ };

  const webApp = {
    ready: noop,
    expand: noop,
    close: noop,
    initData,
    initDataUnsafe: { user, start_param: undefined as string | undefined },
    colorScheme: 'light' as const,
    setHeaderColor: noop,
    setBackgroundColor: noop,
    BackButton: { show: noop, hide: noop, onClick: noop, offClick: noop },
    MainButton: {
      setText: noop, setParams: noop, show: noop, hide: noop,
      onClick: noop, offClick: noop,
    },
    HapticFeedback: {
      impactOccurred: noop, notificationOccurred: noop, selectionChanged: noop,
    },
    shareMessage: (_id: string, cb?: (ok: boolean) => void) => cb?.(true),
    openTelegramLink: (url: string) => window.open(url, '_blank'),
    openLink: (url: string) => window.open(url, '_blank'),
    platform: 'web-mock',
    version: '7.0',
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).Telegram = { WebApp: webApp };
  // eslint-disable-next-line no-console
  console.info('[mockTelegram] installed; user:', user);
}
